// Canonical permission keys. Keep this list in sync with the Settings UI
// and any client-side guard that calls hasPermission().
export const PERMISSION_KEYS = [
  "view_financials",         // payments, arrears, invoices
  "manage_maintenance",      // create/close maintenance tickets
  "cancel_contracts",        // mark contracts as voided / cancelled
  "approve_students",        // flip profile.status from pending_approval -> approved
  "delete_records",          // hard-delete (vs soft-cancel). Most users should never have this.
  "manage_team",             // a delegated owner-lite — careful with this one
  "send_communications",     // bulk email/WhatsApp/SMS sends
  "manage_documents",        // approve/reject student documents
] as const;

export type PermissionKey = (typeof PERMISSION_KEYS)[number];

export const PERMISSION_LABELS: Record<PermissionKey, { label: string; description: string }> = {
  view_financials:       { label: "View financials",      description: "See payments, arrears, invoices." },
  manage_maintenance:    { label: "Manage maintenance",   description: "Create, assign, and close maintenance tickets." },
  cancel_contracts:      { label: "Cancel contracts",     description: "Mark contracts as voided or cancelled." },
  approve_students:      { label: "Approve students",     description: "Verify documents and approve new student onboarding." },
  delete_records:        { label: "Delete records",       description: "Hard-delete records (vs soft-cancel). Use sparingly." },
  manage_team:           { label: "Manage team",          description: "Invite admins/staff and edit their permissions. Owner-equivalent." },
  send_communications:   { label: "Send communications",  description: "Send bulk emails, WhatsApp, SMS to students." },
  manage_documents:      { label: "Manage documents",     description: "Approve, reject, or re-verify student documents." },
};

export type PermissionMap = Partial<Record<PermissionKey, boolean>>;

/**
 * Narrowing-only check. A missing entry is treated as enabled (true).
 * Only an explicit `false` in the map withholds the permission.
 */
export function hasPermission(perms: PermissionMap | undefined | null, key: PermissionKey): boolean {
  if (!perms) return true;
  const v = perms[key];
  return v === undefined ? true : v;
}
